import { TeamRole } from '@showbie-socrative/socrative-utils';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { sendTeacherIntercomEvent } from '~analytics/intercom';
import { EventTypes } from '~analytics/types';
import {
  UseChangeTeamRole,
  useChangeTeamRole,
} from '~teacher/hook/team/useChangeTeamRole';

interface UseHandleChangeToAdminParams {
  teamId: string;
  targetPublicId: string;
  targetTeamRole: TeamRole;
  onMutate?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  onSettled?: () => void;
}

/**
 * Custom hook to handle the change of a team member's role to Admin.
 */
export function useHandleChangeToAdmin({
  onMutate,
  onSuccess,
  onError,
  onSettled,
  teamId,
  targetPublicId,
  targetTeamRole,
}: UseHandleChangeToAdminParams): {
  handleChangeToAdmin: () => void;
  changeRoleMutation: UseChangeTeamRole;
} {
  const changeRoleMutation = useChangeTeamRole();

  /**
   * Function to handle the change of a team member's role to Admin.
   */
  const handleChangeToAdmin = () => {
    // Call the onMutate callback if provided
    onMutate?.();

    // Perform the role change mutation
    changeRoleMutation.mutate(
      {
        teamId: teamId,
        memberPublicId: targetPublicId,
        newRole: TeamRole.Admin,
      },
      {
        onSuccess: () => {
          // Call the onSuccess callback if provided
          onSuccess?.();
          // Send analytics events
          sendTeacherAmplitudeEvent(EventTypes.TeamsChangeRole, {
            team_id: teamId,
            new_role: TeamRole[TeamRole.Admin],
            previous_role: targetTeamRole,
          });
          sendTeacherIntercomEvent(EventTypes.TeamsChangeRole);
        },
        onError: () => {
          // Call the onError callback if provided
          onError?.();
        },
        onSettled: () => {
          // Call the onSettled callback if provided
          onSettled?.();
        },
      }
    );
  };

  return { handleChangeToAdmin, changeRoleMutation };
}
